import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Lock, Target, Plus, CheckCircle, Wallet,
  ArrowRight, Briefcase, GraduationCap, Plane, Home as HomeIcon,
  AlertTriangle, Unlock, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATS = [
  { id: 'rent', label: 'Rent', icon: HomeIcon },
  { id: 'business', label: 'Business', icon: Briefcase },
  { id: 'school', label: 'School', icon: GraduationCap },
  { id: 'travel', label: 'Travel', icon: Plane },
  { id: 'emergency', label: 'Emergency', icon: AlertTriangle },
  { id: 'general', label: 'General', icon: Target },
];

const fmt = (a) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(a).replace('NGN', '\u20a6');

const FutureLock = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('list');
  const [contributing, setContributing] = useState(null);
  const [form, setForm] = useState({ name: '', target_amount: '', duration_months: '', contribution_frequency: 'monthly', goal_category: 'general' });
  const [activePlan, setActivePlan] = useState(null);
  const [earlyBreakPreview, setEarlyBreakPreview] = useState(null);
  const [breaking, setBreaking] = useState(false);

  useEffect(() => { fetchPlans(); }, []);
  const fetchPlans = async () => { try { setPlans((await axios.get(`${API_URL}/api/futurelock/plans`, { headers: getAuthHeaders() })).data); } catch {} finally { setLoading(false); } };

  const openEarlyBreak = async (pid) => {
    try {
      const res = await axios.get(`${API_URL}/api/futurelock/plans/${pid}/early-break-preview`, { headers: getAuthHeaders() });
      setEarlyBreakPreview(res.data);
      setScreen('earlybreak');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    }
  };

  const confirmEarlyBreak = async (pid) => {
    setBreaking(true);
    try {
      const res = await axios.post(`${API_URL}/api/futurelock/plans/${pid}/early-break`, { confirm: true }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setEarlyBreakPreview(null);
      setActivePlan(null);
      setScreen('list');
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to break plan');
    } finally {
      setBreaking(false);
    }
  };

  const calcContrib = () => {
    const t = parseFloat(form.target_amount) || 0;
    const m = parseInt(form.duration_months) || 1;
    const c = form.contribution_frequency === 'weekly' ? m * 4 : m;
    return c > 0 ? Math.ceil(t / c) : 0;
  };

  const handleCreate = async () => {
    if (!form.name || !form.target_amount || !form.duration_months) { toast.error('Fill all fields'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/futurelock/create`, { name: form.name, target_amount: parseFloat(form.target_amount), duration_months: parseInt(form.duration_months), contribution_frequency: form.contribution_frequency, goal_category: form.goal_category }, { headers: getAuthHeaders() });
      toast.success('FutureLock Plan created!');
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setActivePlan(res.data); setScreen('dashboard'); fetchPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleContrib = async (pid) => {
    setContributing(pid);
    try {
      const res = await axios.post(`${API_URL}/api/futurelock/plans/${pid}/contribute`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      if (res.data.completed) { confetti({ particleCount: 200, spread: 120, origin: { y: 0.5 } }); }
      fetchPlans();
      if (activePlan?.id === pid) {
        const u = await axios.get(`${API_URL}/api/futurelock/plans/${pid}`, { headers: getAuthHeaders() });
        setActivePlan(u.data);
        if (u.data.status === 'completed') setScreen('complete');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setContributing(null); }
  };

  const Back = ({ to }) => <button onClick={() => to ? setScreen(to) : navigate(-1)} className="p-2 rounded-full bg-white shadow-sm mb-4"><ArrowLeft size={20} className="text-slate-700" /></button>;

  if (screen === 'list') return <ListScreen plans={plans} loading={loading} fmt={fmt} setScreen={setScreen} setActivePlan={setActivePlan} navigate={navigate} Back={Back} />;
  if (screen === 'entry') return <EntryScreen setScreen={setScreen} Back={Back} />;
  if (screen === 'setup') return <SetupScreen form={form} setForm={setForm} fmt={fmt} calcContrib={calcContrib} setScreen={setScreen} Back={Back} />;
  if (screen === 'commitment') return <CommitScreen form={form} fmt={fmt} calcContrib={calcContrib} setScreen={setScreen} handleCreate={handleCreate} Back={Back} />;
  if (screen === 'dashboard' && activePlan) return <DashScreen plan={activePlan} fmt={fmt} contributing={contributing} handleContrib={handleContrib} setScreen={setScreen} setActivePlan={setActivePlan} openEarlyBreak={openEarlyBreak} Back={Back} />;
  if (screen === 'earlybreak' && activePlan && earlyBreakPreview) return <EarlyBreakScreen plan={activePlan} preview={earlyBreakPreview} fmt={fmt} breaking={breaking} confirmEarlyBreak={confirmEarlyBreak} setScreen={setScreen} />;
  if (screen === 'complete' && activePlan) return <CompleteScreen plan={activePlan} fmt={fmt} setScreen={setScreen} setActivePlan={setActivePlan} Back={Back} />;
  return null;
};

const ListScreen = ({ plans, loading, fmt, setScreen, setActivePlan, navigate, Back }) => (
  <div className="min-h-screen bg-stone-50 pb-24" data-testid="futurelock-page">
    <div className="app-container px-4 pt-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white shadow-sm"><ArrowLeft size={20} className="text-slate-700" /></button>
        <div><h1 className="text-xl font-bold text-slate-900">FutureLock Plans</h1><p className="text-xs text-slate-500">Structured savings with discipline</p></div>
      </div>
      <Card className="mb-6 bg-gradient-to-r from-[#0B1F3A] to-[#2272C5] text-white" data-testid="futurelock-cta">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2"><Lock size={20} /><span className="font-bold text-lg">FutureLock Plan</span></div>
          <p className="text-sm text-white/80 mb-4">Save towards your goal with structure and discipline. Funds are locked until completion.</p>
          <Button onClick={() => setScreen('entry')} className="bg-white text-[#0B1F3A] hover:bg-white/90 font-bold w-full" data-testid="start-plan-btn"><Plus size={16} className="mr-2" />Start New Plan</Button>
        </CardContent>
      </Card>
      {loading ? <Skeleton className="h-32 rounded-2xl" /> : plans.length === 0 ? (
        <div className="text-center py-8"><Lock size={48} className="mx-auto text-slate-300 mb-3" /><p className="text-sm text-slate-400">No plans yet. Start your first FutureLock!</p></div>
      ) : (
        <div className="space-y-3">{plans.map(p => {
          const pct = p.target_amount > 0 ? Math.round((p.current_amount / p.target_amount) * 100) : 0;
          const Cat = CATS.find(c => c.id === p.goal_category)?.icon || Target;
          return (
            <Card key={p.id} className="glass-card cursor-pointer" onClick={() => { setActivePlan(p); setScreen(p.status === 'completed' ? 'complete' : 'dashboard'); }} data-testid={`plan-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3"><div className="p-2 rounded-xl bg-[#0B1F3A]/10"><Cat size={18} className="text-[#0B1F3A]" /></div><div><p className="font-bold text-slate-900 text-sm">{p.name}</p><p className="text-[10px] text-slate-500">{p.contribution_frequency} &middot; {p.duration_months}mo</p></div></div>
                  <Badge className={p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>{p.status === 'completed' ? 'Complete' : `${pct}%`}</Badge>
                </div>
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-2"><div className={`h-full rounded-full ${p.status === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#0B1F3A] to-[#2272C5]'}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                <div className="flex justify-between text-xs text-slate-500"><span>{fmt(p.current_amount)} saved</span><span>{fmt(p.target_amount)} target</span></div>
              </CardContent>
            </Card>
          );
        })}</div>
      )}
    </div><BottomNav />
  </div>
);

const EntryScreen = ({ setScreen, Back }) => (
  <div className="min-h-screen bg-stone-50 pb-24" data-testid="futurelock-entry">
    <div className="app-container px-4 pt-6">
      <Back to="list" />
      <div className="text-center mb-8">
        <div className="p-4 rounded-full bg-[#0B1F3A]/10 inline-block mb-3"><Lock size={32} className="text-[#0B1F3A]" /></div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">FutureLock Plan</h1>
        <p className="text-sm text-slate-500">Save towards your goal with structure and discipline.</p>
      </div>
      <Button onClick={() => setScreen('setup')} className="w-full h-14 bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white rounded-2xl font-bold text-base" data-testid="start-setup-btn"><Target size={18} className="mr-2" />Set Up Your Plan</Button>
    </div><BottomNav />
  </div>
);

const SetupScreen = ({ form, setForm, fmt, calcContrib, setScreen, Back }) => (
  <div className="min-h-screen bg-stone-50 pb-32" data-testid="futurelock-setup">
    <div className="app-container px-4 pt-6">
      <Back to="entry" />
      <h2 className="text-xl font-bold text-slate-900 mb-1">What are you saving for?</h2>
      <p className="text-sm text-slate-500 mb-6">Set your goal and we'll create your plan.</p>
      <p className="text-sm font-semibold text-slate-700 mb-2">Category</p>
      <div className="grid grid-cols-3 gap-2 mb-5">{CATS.map(c => { const I = c.icon; return (
        <button key={c.id} onClick={() => setForm({ ...form, goal_category: c.id })} className={`p-3 rounded-xl border-2 text-center ${form.goal_category === c.id ? 'border-[#0B1F3A] bg-[#0B1F3A]/5' : 'border-slate-200'}`} data-testid={`cat-${c.id}`}>
          <I size={18} className={`mx-auto mb-1 ${form.goal_category === c.id ? 'text-[#0B1F3A]' : 'text-slate-400'}`} /><span className="text-[10px] font-medium text-slate-700">{c.label}</span>
        </button>);
      })}</div>
      <div className="space-y-4 mb-6">
        <div><p className="text-sm font-semibold text-slate-700 mb-1">Plan Name</p><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. House Rent 2026" className="rounded-xl" data-testid="fl-name" /></div>
        <div><p className="text-sm font-semibold text-slate-700 mb-1">Goal Amount</p><Input type="number" value={form.target_amount} onChange={e => setForm({ ...form, target_amount: e.target.value })} placeholder="50000" className="rounded-xl" data-testid="fl-target" /></div>
        <div><p className="text-sm font-semibold text-slate-700 mb-1">Duration (months)</p><Input type="number" value={form.duration_months} onChange={e => setForm({ ...form, duration_months: e.target.value })} placeholder="6" className="rounded-xl" data-testid="fl-duration" /></div>
        <div><p className="text-sm font-semibold text-slate-700 mb-1">Frequency</p>
          <div className="grid grid-cols-2 gap-2">{['weekly', 'monthly'].map(f => (
            <button key={f} onClick={() => setForm({ ...form, contribution_frequency: f })} className={`p-3 rounded-xl border-2 text-sm font-medium ${form.contribution_frequency === f ? 'border-[#0B1F3A] bg-[#0B1F3A]/5 text-[#0B1F3A]' : 'border-slate-200 text-slate-600'}`} data-testid={`freq-${f}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}</div>
        </div>
      </div>
      {form.target_amount && form.duration_months && (
        <Card className="glass-card border-2 border-[#2272C5]/30 mb-6" data-testid="fl-preview"><CardContent className="p-4 text-center">
          <p className="text-sm text-slate-500 mb-1">Your contribution will be</p>
          <p className="text-3xl font-black text-[#0B1F3A]">{fmt(calcContrib())}</p>
          <p className="text-sm text-slate-500">per {form.contribution_frequency === 'weekly' ? 'week' : 'month'}</p>
        </CardContent></Card>
      )}
      <Button onClick={() => { if (!form.name || !form.target_amount || !form.duration_months) { toast.error('Fill all fields'); return; } setScreen('commitment'); }} className="w-full h-14 bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white rounded-2xl font-bold" data-testid="fl-next-btn"><ArrowRight size={18} className="mr-2" />Continue</Button>
    </div><BottomNav />
  </div>
);

const CommitScreen = ({ form, fmt, calcContrib, setScreen, handleCreate, Back }) => (
  <div className="min-h-screen bg-stone-50 pb-32" data-testid="futurelock-commitment">
    <div className="app-container px-4 pt-6">
      <Back to="setup" />
      <div className="text-center mb-8">
        <div className="p-4 rounded-full bg-amber-100 inline-block mb-3"><Lock size={32} className="text-amber-600" /></div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Your plan is locked</h2>
        <p className="text-sm text-slate-500">Here's what you're committing to:</p>
      </div>
      <Card className="glass-card mb-6"><CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-3"><CheckCircle size={18} className="text-emerald-500 flex-shrink-0" /><p className="text-sm text-slate-700">Fixed {form.contribution_frequency} contributions of <strong>{fmt(calcContrib())}</strong></p></div>
        <div className="flex items-center gap-3"><CheckCircle size={18} className="text-emerald-500 flex-shrink-0" /><p className="text-sm text-slate-700">Funds locked until plan completion</p></div>
        <div className="flex items-center gap-3"><CheckCircle size={18} className="text-emerald-500 flex-shrink-0" /><p className="text-sm text-slate-700">Full payout of <strong>{fmt(parseFloat(form.target_amount) || 0)}</strong> at end</p></div>
        <div className="flex items-center gap-3"><AlertTriangle size={18} className="text-amber-500 flex-shrink-0" /><p className="text-sm text-slate-700">Early withdrawal is restricted</p></div>
      </CardContent></Card>
      <Button onClick={handleCreate} className="w-full h-14 bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white rounded-2xl font-bold" data-testid="fl-confirm-btn"><Lock size={18} className="mr-2" />Confirm Plan</Button>
    </div><BottomNav />
  </div>
);

const DashScreen = ({ plan, fmt, contributing, handleContrib, setScreen, setActivePlan, openEarlyBreak, Back }) => {
  const pct = plan.target_amount > 0 ? Math.round((plan.current_amount / plan.target_amount) * 100) : 0;
  const rem = plan.target_amount - plan.current_amount;
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="futurelock-dashboard">
      <div className="app-container px-4 pt-6">
        <button onClick={() => { setScreen('list'); setActivePlan(null); }} className="p-2 rounded-full bg-white shadow-sm mb-4"><ArrowLeft size={20} className="text-slate-700" /></button>
        <h2 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h2>
        <p className="text-xs text-slate-500 mb-6">{plan.goal_category} &middot; {plan.contribution_frequency}</p>
        <Card className="glass-card mb-4" data-testid="fl-progress"><CardContent className="p-6 text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg viewBox="0 0 36 36" className="w-full h-full"><path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3" /><path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#0B1F3A" strokeWidth="3" strokeDasharray={`${pct}, 100`} strokeLinecap="round" /></svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-2xl font-black text-[#0B1F3A]">{pct}%</span><span className="text-[10px] text-slate-500">complete</span></div>
          </div>
          <p className="text-lg font-bold text-[#0B1F3A]">{fmt(plan.current_amount)} <span className="text-sm font-normal text-slate-400">/ {fmt(plan.target_amount)}</span></p>
          {rem > 0 && <p className="text-xs text-slate-500 mt-1">{fmt(rem)} remaining</p>}
          <p className="text-xs text-slate-400 mt-2">{plan.cycles_completed} of {plan.total_cycles} contributions</p>
        </CardContent></Card>
        <Card className="glass-card mb-4 border-l-4 border-l-[#2272C5]"><CardContent className="p-4">
          <p className="text-sm text-slate-500 mb-1">Next Contribution</p>
          <p className="text-xl font-bold text-[#0B1F3A]">{fmt(plan.contribution_per_cycle)}</p>
          <p className="text-xs text-slate-400">per {plan.contribution_frequency === 'weekly' ? 'week' : 'month'}</p>
        </CardContent></Card>
        {plan.status === 'active' && <Button onClick={() => handleContrib(plan.id)} disabled={contributing === plan.id} className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold" data-testid="fl-contribute-btn"><Wallet size={18} className="mr-2" />{contributing === plan.id ? 'Processing...' : `Contribute ${fmt(plan.contribution_per_cycle)}`}</Button>}
        {plan.status === 'active' && plan.current_amount > 0 && (
          <Button
            onClick={() => openEarlyBreak(plan.id)}
            variant="outline"
            className="w-full h-12 mt-3 rounded-2xl border-2 border-red-300 text-red-600 hover:bg-red-50 font-bold"
            data-testid="fl-early-break-btn"
          >
            <Unlock size={16} className="mr-2" />
            Break Plan Early
          </Button>
        )}
      </div><BottomNav />
    </div>
  );
};

const EarlyBreakScreen = ({ plan, preview, fmt, breaking, confirmEarlyBreak, setScreen }) => {
  const penaltyPctLabel = Math.round((preview.penalty_rate || 0) * 100);
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="futurelock-earlybreak">
      <div className="app-container px-4 pt-6">
        <button onClick={() => setScreen('dashboard')} className="p-2 rounded-full bg-white shadow-sm mb-4" data-testid="fl-earlybreak-back">
          <ArrowLeft size={20} className="text-slate-700" />
        </button>
        <div className="text-center mb-6">
          <div className="p-4 rounded-full bg-red-100 inline-block mb-3">
            <AlertTriangle size={32} className="text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1">Break this plan?</h2>
          <p className="text-sm text-slate-500">A {penaltyPctLabel}% early-break fee will be applied on your locked amount.</p>
        </div>

        <Card className="glass-card mb-4 border-2 border-red-200" data-testid="fl-earlybreak-preview">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <span className="text-sm font-semibold text-slate-600">Currently locked</span>
              <span className="font-black text-[#0B1F3A]">{fmt(preview.locked_amount)}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-red-50 border border-red-200">
              <span className="text-sm font-semibold text-red-700">Early-break fee ({penaltyPctLabel}%)</span>
              <span className="font-black text-red-700">- {fmt(preview.penalty_amount)}</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-50 border-2 border-emerald-300">
              <span className="text-sm font-black text-emerald-800">You'll receive</span>
              <span className="font-black text-emerald-800 text-xl">{fmt(preview.you_receive)}</span>
            </div>
            <div className="pt-2 border-t border-slate-200 space-y-1 text-xs text-slate-500">
              <p>Plan: <span className="font-semibold text-slate-700">{preview.plan_name}</span></p>
              <p>Progress: <span className="font-semibold text-slate-700">{preview.progress_pct}% ({preview.cycles_completed}/{preview.cycles_completed + preview.cycles_remaining} cycles)</span></p>
              <p>Goal: <span className="font-semibold text-slate-700">{fmt(preview.target_amount)}</span></p>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-4 bg-amber-50 border-2 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">This action cannot be undone.</p>
              <p className="text-xs text-amber-700 font-medium mt-0.5">The plan will be permanently marked as broken. The fee is non-refundable.</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button
            onClick={() => setScreen('dashboard')}
            variant="outline"
            className="flex-1 h-14 rounded-2xl font-bold"
            data-testid="fl-earlybreak-cancel"
          >
            Keep Saving
          </Button>
          <Button
            onClick={() => confirmEarlyBreak(plan.id)}
            disabled={breaking}
            className="flex-1 h-14 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold"
            data-testid="fl-earlybreak-confirm"
          >
            <XCircle size={18} className="mr-2" />
            {breaking ? 'Breaking...' : 'Break Now'}
          </Button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

const CompleteScreen = ({ plan, fmt, setScreen, setActivePlan, Back }) => (
  <div className="min-h-screen bg-stone-50 pb-24" data-testid="futurelock-complete">
    <div className="app-container px-4 pt-6">
      <div className="text-center mt-8">
        <div className="p-5 rounded-full bg-emerald-100 inline-block mb-4"><CheckCircle size={48} className="text-emerald-600" /></div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Goal Achieved!</h2>
        <p className="text-sm text-slate-500 mb-6">You successfully completed your plan.</p>
        <Card className="glass-card mb-6"><CardContent className="p-6 text-center">
          <p className="text-sm text-slate-500">Total Saved</p>
          <p className="text-3xl font-black text-emerald-600">{fmt(plan.current_amount)}</p>
          <p className="text-xs text-slate-400 mt-2">{plan.cycles_completed} contributions over {plan.duration_months} months</p>
        </CardContent></Card>
        <Button onClick={() => { setScreen('list'); setActivePlan(null); }} className="w-full h-14 bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white rounded-2xl font-bold" data-testid="fl-back-btn">Back to Plans</Button>
      </div>
    </div><BottomNav />
  </div>
);

export default FutureLock;
